import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle, CircleNotch, PaintBrush, Lock, ArrowRight, SignIn, ShieldWarning, PaperPlaneTilt, Warning } from "@phosphor-icons/react";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import {
  getOrder,
  updateCardInstanceDesign,
  uploadDesignImage,
  submitOrderForPrinting,
} from "@/lib/orders.functions";
import bananaLogo from "@/assets/banana-cards-logo.png";
import metalPlaceholder from "@/assets/placeholders/metal.webp";
import bambooPlaceholder from "@/assets/placeholders/bamboo.webp";
import whitePvcPlaceholder from "@/assets/placeholders/white-pvc.webp";
import blackPvcPlaceholder from "@/assets/placeholders/black-pvc.webp";
import goldPlaceholder from "@/assets/placeholders/gold.webp";
import googleReviewPlaceholder from "@/assets/placeholders/google-review.png";

export const Route = createFileRoute("/orders/$id_/customize")({
  component: CustomizePage,
});

const PLACEHOLDER_IMAGES: Record<string, string> = {
  metal: metalPlaceholder,
  bamboo: bambooPlaceholder,
  "white-pvc": whitePvcPlaceholder,
  "black-pvc": blackPvcPlaceholder,
  "24kt-gold": goldPlaceholder,
  "google-review": googleReviewPlaceholder,
};

const MATERIAL_MAP: Record<string, string> = {
  metal: "metal",
  bamboo: "bamboo",
  "white-pvc": "white-pvc",
  "24kt-gold": "gold",
};

const NON_CUSTOMIZABLE = new Set(["google-review"]);

type CardInstance = {
  id: string;
  product_slug: string;
  product_name: string;
  status: string;
  customization: null | {
    previewUrl: string;
    printUrl: string;
    htmlConfig: Record<string, unknown>;
  };
};

function CustomizePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const fetchOrder = useServerFn(getOrder);
  const updateFn = useServerFn(updateCardInstanceDesign);
  const uploadFn = useServerFn(uploadDesignImage);
  const submitFn = useServerFn(submitOrderForPrinting);

  const [authState, setAuthState] = useState<"loading" | "signed-out" | "signed-in">("loading");
  useEffect(() => {
    let mounted = true;
    supabase.auth.getUser().then(({ data }) => {
      if (!mounted) return;
      setAuthState(data.user ? "signed-in" : "signed-out");
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthState(session?.user ? "signed-in" : "signed-out");
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const { data, isLoading, refetch, error: orderError } = useQuery({
    queryKey: ["order", id],
    queryFn: () => fetchOrder({ data: { id } }),
    enabled: authState === "signed-in",
    retry: false,
  });

  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});
  const [updatedCardIds, setUpdatedCardIds] = useState<Set<string>>(new Set());
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const editorReadyRef = useRef(false);
  const pendingPayloadRef = useRef<unknown>(null);

  const order = data?.order as
    | (Record<string, unknown> & { card_instances: CardInstance[]; status: string; order_number: string })
    | undefined;

  type Rejection = { reason: string; note: string | null; created_at: string };
  const rejections: Record<string, Rejection[]> =
    ((data as any)?.rejections as Record<string, Rejection[]> | undefined) ?? {};
  const hasRejections = Object.keys(rejections).length > 0;
  const totalRejections = Object.values(rejections).reduce((n, arr) => n + arr.length, 0);

  const instances: CardInstance[] = useMemo(
    () => (Array.isArray(order?.card_instances) ? (order!.card_instances as CardInstance[]) : []),
    [order],
  );

  const customizable = instances.filter((c) => !NON_CUSTOMIZABLE.has(c.product_slug));
  const designed = customizable.filter((c) => c.customization).length;
  const total = customizable.length;
  const requiresDesign = total > 0;
  const allDone = !requiresDesign || designed === total;
  const locked =
    order?.status === "to_be_printed" ||
    order?.status === "printing" ||
    order?.status === "ready_to_ship" ||
    order?.status === "shipped" ||
    order?.status === "delivered";

  const activeCard = useMemo(() => instances.find((c) => c.id === activeCardId) ?? null, [instances, activeCardId]);

  // Iframe message handler
  useEffect(() => {
    const onMsg = async (e: MessageEvent) => {
      // Only accept messages from our own origin (the card-designer iframe).
      if (e.origin !== window.location.origin) return;
      if (!e.data || typeof e.data !== "object") return;
      const { type, payload } = e.data as { type?: string; payload?: any };
      if (type === "EDITOR_READY") {
        editorReadyRef.current = true;
        if (pendingPayloadRef.current && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            { type: "INITIALIZE_EDITOR", payload: pendingPayloadRef.current },
            "*",
          );
        }
      } else if (type === "DESIGN_CANCELLED") {
        setActiveCardId(null);
      } else if (type === "DESIGN_COMPLETED" && payload && activeCardId) {
        await handleDesignCompleted(payload);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCardId, id]);

  // When modal opens, send payload (or buffer until EDITOR_READY)
  useEffect(() => {
    if (!activeCard) {
      editorReadyRef.current = false;
      pendingPayloadRef.current = null;
      return;
    }
    const material = MATERIAL_MAP[activeCard.product_slug] ?? "white-pvc";
    const payload: { material: string; config?: Record<string, unknown> } = { material };
    if (activeCard.customization?.htmlConfig) {
      payload.config = activeCard.customization.htmlConfig;
    }
    pendingPayloadRef.current = payload;
    if (editorReadyRef.current && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: "INITIALIZE_EDITOR", payload }, "*");
    }
  }, [activeCard]);

  async function handleDesignCompleted(payload: {
    printDesign: string;
    previewDesign: string;
    config: Record<string, any>;
  }) {
    if (!activeCardId) return;
    setSaving(true);
    setErr(null);
    try {
      const cfg: Record<string, any> = { ...payload.config };

      // Upload any base64 assets to storage, swap to URLs
      if (cfg.logoBase64) {
        const r = await uploadFn({
          data: { orderId: id, cardInstanceId: activeCardId, kind: "logo", base64: cfg.logoBase64 },
        });
        cfg.logoUrl = r.url;
        delete cfg.logoBase64;
      }
      if (cfg.bgBase64) {
        const r = await uploadFn({
          data: { orderId: id, cardInstanceId: activeCardId, kind: "bg", base64: cfg.bgBase64 },
        });
        cfg.bgUrl = r.url;
        delete cfg.bgBase64;
      }
      if (cfg.fullDesignBase64) {
        const r = await uploadFn({
          data: { orderId: id, cardInstanceId: activeCardId, kind: "full", base64: cfg.fullDesignBase64 },
        });
        cfg.fullDesignUrl = r.url;
        delete cfg.fullDesignBase64;
      }

      const [previewRes, printRes] = await Promise.all([
        uploadFn({
          data: { orderId: id, cardInstanceId: activeCardId, kind: "preview", base64: payload.previewDesign },
        }),
        uploadFn({
          data: { orderId: id, cardInstanceId: activeCardId, kind: "print", base64: payload.printDesign },
        }),
      ]);

      await updateFn({
        data: {
          orderId: id,
          cardInstanceId: activeCardId,
          customization: {
            previewUrl: previewRes.url,
            printUrl: printRes.url,
            htmlConfig: cfg,
          },
        },
      });
      setImageLoading((m) => ({ ...m, [activeCardId]: true }));
      await refetch();
      setUpdatedCardIds((prev) => new Set(prev).add(activeCardId));
      setActiveCardId(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to save design");
    } finally {
      setSaving(false);
    }
  }

  async function onSubmit() {
    setSubmitting(true);
    setErr(null);
    try {
      const res = await submitFn({ data: { orderId: id } });
      if (res?.status === "ready_to_ship") {
        navigate({ to: "/orders/$id/status", params: { id } });
        return;
      }
      await refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not submit order");
    } finally {
      setSubmitting(false);
    }
  }

  if (authState === "loading" || (authState === "signed-in" && isLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <CircleNotch className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (authState === "signed-out") {
    const redirectTo = typeof window !== "undefined" ? window.location.pathname : `/orders/${id}/customize`;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card p-8">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <SignIn className="h-6 w-6" />
          </div>
          <h1 className="font-display text-2xl mb-2">Sign in to manage your order</h1>
          <p className="text-sm text-muted-foreground mb-6">
            You need to be logged in to your account to design and manage this order.
          </p>
          <a
            href={`/orders/claim?id=${encodeURIComponent(id)}&redirect=${encodeURIComponent(redirectTo)}`}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background transition hover:opacity-90"
          >
            <SignIn className="h-4 w-4" />
            Sign in
          </a>
          <div className="mt-4">
            <Link to="/" className="text-xs text-muted-foreground underline">Go home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (orderError || !order) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card p-8">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <ShieldWarning className="h-6 w-6" />
          </div>
          <h1 className="font-display text-2xl mb-2">This order isn't linked to your account</h1>
          <p className="text-sm text-muted-foreground mb-6">
            We couldn't find this order under your account. If you paid with a different email,
            sign in with that account. If something looks off, please contact us and we'll sort it out.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <a
              href="mailto:support@banana.cards"
              className="inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background transition hover:opacity-90"
            >
              Contact support
            </a>
            <button
              onClick={async () => { await supabase.auth.signOut(); }}
              className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-5 py-2.5 text-sm font-bold text-foreground transition hover:bg-muted"
            >
              Sign in with a different account
            </button>
          </div>
          <div className="mt-4">
            <Link to="/" className="text-xs text-muted-foreground underline">Go home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-center px-4">
        <div>
          <h1 className="font-display text-2xl mb-2">Order not found</h1>
          <Link to="/" className="text-sm underline">Go home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-5xl px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <img src={bananaLogo} alt="banana.cards" className="h-3.5" />
          </Link>
          <div className="text-[11px] sm:text-xs text-muted-foreground truncate">
            <span className="hidden sm:inline">Order </span>
            <span className="font-mono font-bold text-foreground">{order.order_number}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:py-10 pb-28 sm:pb-10">
        {locked && (
          <Link
            to="/orders/$id/status"
            params={{ id }}
            className="mb-6 sm:mb-8 flex items-center justify-between gap-3 rounded-2xl border border-foreground/20 bg-foreground text-background px-4 sm:px-5 py-4 hover:opacity-95 transition"
          >
            <div>
              <div className="font-bold text-sm sm:text-base">
                {requiresDesign
                  ? "Your designs have been submitted!"
                  : "Your order has been submitted for printing!"}
              </div>
              <div className="text-xs sm:text-sm opacity-80 mt-0.5">View order status</div>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0" weight="bold" />
          </Link>
        )}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl mb-2">Design your cards</h1>
          <p className="text-[13px] sm:text-sm text-muted-foreground">
            {locked
              ? requiresDesign
                ? "Your designs have been submitted to print and can no longer be edited."
                : "Your order has been submitted and is now being fulfilled."
              : requiresDesign
                ? "Click each card below to open the designer. Your designs save automatically."
                : "Your order is ready to go. Review the cards below and submit whenever you're ready."}
          </p>
        </div>

        {!requiresDesign && !locked && (
          <div className="mb-6 rounded-2xl border-2 border-foreground bg-foreground text-background px-5 py-5 sm:px-6 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <PaperPlaneTilt className="h-6 w-6 shrink-0 mt-0.5" weight="fill" />
              <div>
                <div className="font-bold text-base sm:text-lg">No design needed for this order</div>
                <div className="text-xs sm:text-sm opacity-80 mt-1">
                  Your cards are ready to print — submit now and we'll start fulfilling right away. You'll get a tracking number as soon as it ships.
                </div>
              </div>
            </div>
            <button
              onClick={onSubmit}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-background px-6 py-3 text-sm font-bold text-foreground transition disabled:opacity-50 shrink-0"
            >
              {submitting ? (
                <>
                  <CircleNotch className="h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  Submit order
                  <ArrowRight className="h-4 w-4" weight="bold" />
                </>
              )}
            </button>
          </div>
        )}

        {requiresDesign && (
        <div className="mb-6 hidden sm:flex items-center justify-between rounded-2xl border border-border bg-card px-5 py-4">
          <div className="text-sm">
            <span className="font-bold">{designed}</span>
            <span className="text-muted-foreground"> of </span>
            <span className="font-bold">{total}</span>
            <span className="text-muted-foreground"> cards designed</span>
          </div>
          <button
            onClick={onSubmit}
            disabled={!allDone || locked || submitting}
            className="inline-flex items-center justify-center rounded-xl bg-foreground px-6 py-2.5 text-sm font-bold text-background transition disabled:opacity-50"
          >
            {locked ? "Submitted" : submitting ? "Submitting…" : "Submit for printing"}
          </button>
        </div>
        )}

        {err && (
          <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {err}
          </div>
        )}

        {hasRejections && !locked && (
          <div className="mb-6 rounded-2xl border-2 border-destructive/40 bg-destructive/10 px-4 py-4 sm:px-5 sm:py-5">
            <div className="flex items-start gap-3">
              <Warning className="h-5 w-5 shrink-0 mt-0.5 text-destructive" weight="fill" />
              <div className="flex-1">
                <div className="font-bold text-sm sm:text-base text-destructive">
                  {totalRejections === 1
                    ? "Our print team flagged 1 issue with your designs"
                    : `Our print team flagged ${totalRejections} issues with your designs`}
                </div>
                <div className="text-xs sm:text-sm text-destructive/80 mt-1">
                  Fix the cards marked in red below and resubmit — you'll get a fresh confirmation email once we receive them.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {instances.map((ci) => {
            const nonCustom = NON_CUSTOMIZABLE.has(ci.product_slug);
            const baseImg = ci.customization?.previewUrl ?? PLACEHOLDER_IMAGES[ci.product_slug];
            const cardRejections = rejections[ci.id] ?? [];
            const isUpdated = updatedCardIds.has(ci.id);
            const isRejected = !locked && cardRejections.length > 0 && !isUpdated;
            return (
              <div
                key={ci.id}
                className={
                  isRejected
                    ? "rounded-2xl border-2 border-destructive bg-destructive/5 overflow-hidden flex flex-col"
                    : "rounded-2xl border border-border bg-card overflow-hidden flex flex-col"
                }
              >
                <div className="aspect-[1.586/1] bg-muted flex items-center justify-center overflow-hidden relative p-5">
                  {imageLoading[ci.id] && (
                    <Skeleton className="absolute inset-5 rounded-xl z-10" />
                  )}
                  {baseImg ? (
                    <img
                      key={baseImg}
                      src={baseImg}
                      alt={ci.product_name}
                      onLoad={() => setImageLoading((m) => ({ ...m, [ci.id]: false }))}
                      onError={() => setImageLoading((m) => ({ ...m, [ci.id]: false }))}
                      className={
                        isRejected
                          ? "w-full h-full object-cover rounded-xl ring-2 ring-destructive"
                          : "w-full h-full object-cover rounded-xl ring-1 ring-border"
                      }
                    />
                  ) : (
                    <div className="text-xs text-muted-foreground">No preview</div>
                  )}
                  {isRejected && (
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-destructive px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-destructive-foreground shadow">
                      <Warning className="h-3 w-3" weight="fill" />
                      Needs fix
                    </div>
                  )}
                </div>
                <div className="p-3 sm:p-4 flex-1 flex flex-col gap-3">
                  <div>
                    <div className="text-sm font-bold truncate">{ci.product_name}</div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">
                      ID {ci.id.slice(0, 8)}
                    </div>
                  </div>
                  {isRejected && (
                    <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-destructive mb-1.5">
                        What we flagged
                      </div>
                      <ul className="space-y-2">
                        {cardRejections.map((r, i) => (
                          <li key={i} className="text-xs leading-snug">
                            <div className="font-bold text-destructive">{r.reason}</div>
                            {r.note && (
                              <div className="text-destructive/80 mt-0.5">{r.note}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {isUpdated && !locked && cardRejections.length > 0 && (
                    <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-3">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-yellow-700 mb-1">
                        Design updated
                      </div>
                      <div className="text-xs text-yellow-700">
                        Please submit your new designs so our print team can review them.
                      </div>
                    </div>
                  )}
                  {nonCustom ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Lock className="h-3.5 w-3.5" />
                      Not customizable
                    </div>
                  ) : ci.customization ? (
                    <div className="flex items-center gap-2 text-xs">
                      <CheckCircle className="h-4 w-4 text-foreground" weight="fill" />
                      <span className="font-medium">Design saved</span>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">Pending design</div>
                  )}
                  {!nonCustom && !locked && (
                    <button
                      onClick={() => setActiveCardId(ci.id)}
                      className={
                        isRejected
                          ? "mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-destructive px-4 py-2.5 text-xs font-bold text-destructive-foreground active:scale-[0.98] transition"
                          : "mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-foreground px-4 py-2.5 text-xs font-bold text-background active:scale-[0.98] transition"
                      }
                    >
                      <PaintBrush className="h-3.5 w-3.5" />
                      {isRejected ? "Fix design" : ci.customization ? "Edit design" : "Design card"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Mobile sticky submit bar */}
      {requiresDesign && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur border-t border-border px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-xs">
            <span className="font-bold">{designed}</span>
            <span className="text-muted-foreground"> / </span>
            <span className="font-bold">{total}</span>
            <span className="text-muted-foreground"> designed</span>
          </div>
          <button
            onClick={onSubmit}
            disabled={!allDone || locked || submitting}
            className="inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-2.5 text-xs font-bold text-background transition disabled:opacity-50"
          >
            {locked ? "Submitted" : submitting ? "Submitting…" : "Submit for printing"}
          </button>
        </div>
      )}

      {activeCard && (
        <div className="fixed inset-0 z-50 bg-black flex items-stretch">
          <div className="w-full h-[100dvh] flex flex-col bg-white">
            <div className="flex-1 relative min-h-0">
              <iframe
                ref={iframeRef}
                src="/card-designer.html"
                title="Card designer"
                className="w-full h-full border-0 block"
                allow="clipboard-write"
              />
              {saving && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                  <div className="flex items-center gap-3 text-sm font-bold">
                    <CircleNotch className="h-5 w-5 animate-spin" />
                    Saving your design…
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
